import { z } from 'zod';
import { idSchema, isoDateSchema, memberSummarySchema } from './common';
import { sessionStatusSchema } from '../enums';
import { rumViewSchema } from './rum';
/** US-11 — une séance compte au moins un rhum. Sans bouteille, il n'y a rien à déguster. */
export const MIN_RUMS_PER_SESSION = 1;
/**
 * Il n'y a **pas** de maximum métier de rhums par séance : le cercle en aligne autant qu'il
 * veut, et la borne « trois » des premières maquettes était une hypothèse de cadrage, pas
 * une règle du cercle — la première séance réelle l'a montré.
 *
 * Cette borne-ci ne protège que de la charge absurde : un `orderIndex` à 10 000 ou un
 * réordonnancement de mille identifiants ne vient pas d'un usage réel, et zod a besoin d'un
 * plafond pour le refuser. La nommer « hard cap » plutôt que « max » est délibéré : celui
 * qui la relira ne doit pas la prendre pour la règle métier qu'on vient d'enlever.
 */
export const RUMS_PER_SESSION_HARD_CAP = 100;
export const createSessionSchema = z.object({
    startsAt: isoDateSchema,
    endsAt: isoDateSchema.nullish(),
    location: z.string().trim().max(120).nullish(),
});
export const updateSessionSchema = createSessionSchema.partial().extend({
    status: sessionStatusSchema.optional(),
});
/** Écran 10 — « + Rattacher un rhum », avec position dans l'ordre de dégustation. */
export const attachRumSchema = z.object({
    rumId: idSchema,
    orderIndex: z.number().int().min(1).max(RUMS_PER_SESSION_HARD_CAP),
    isBlind: z.boolean().default(false),
});
/** Écran 10 — l'interrupteur doré, rhum par rhum (US-13). */
export const setBlindModeSchema = z.object({
    isBlind: z.boolean(),
});
/** US-12 — présence et invités. La borne haute vient du Setting `maxGuests`. */
export const setAttendanceSchema = z.object({
    confirmed: z.boolean(),
    guestCount: z.number().int().min(0).max(10).default(0),
});
export const sessionRumViewSchema = z.object({
    orderIndex: z.number().int(),
    isBlind: z.boolean(),
    revealedAt: isoDateSchema.nullable(),
    rum: rumViewSchema,
    /** Le membre courant a-t-il déjà validé sa note sur ce rhum ? Pilote l'écran 04. */
    hasOwnValidatedTasting: z.boolean(),
    hasOwnLockedGuess: z.boolean(),
});
export const attendanceViewSchema = z.object({
    member: memberSummarySchema,
    confirmed: z.boolean(),
    guestCount: z.number().int(),
});
/** Écran 01 et écran 10 — la séance dans son intégralité. */
export const sessionViewSchema = z.object({
    id: idSchema,
    number: z.number().int(),
    startsAt: isoDateSchema,
    endsAt: isoDateSchema.nullable(),
    location: z.string().nullable(),
    status: sessionStatusSchema,
    rums: z.array(sessionRumViewSchema),
    attendances: z.array(attendanceViewSchema),
    /** Avancement affiché en régie : « Notes déposées 5/6 · Pronostics 4/6 ». */
    progress: z.object({
        expectedTasters: z.number().int(),
        validatedTastings: z.number().int(),
        lockedGuesses: z.number().int(),
    }),
    maxGuests: z.number().int(),
    closedAt: isoDateSchema.nullable(),
});
// ------------------------------------------------------------------ récap (US-16 / US-17)
export const sessionRecapSchema = z.object({
    sessionId: idSchema,
    number: z.number().int(),
    startsAt: isoDateSchema,
    podium: z.array(z.object({
        rank: z.number().int(),
        rum: rumViewSchema,
        average: z.number().int(),
    })),
    /** « Coup de cœur du club » : le rhum en tête du podium. */
    clubFavouriteRumId: idSchema.nullable(),
    /** Le commentaire le mieux noté de la séance, retenu pour le badge « plume ». */
    bestComment: z
        .object({
        memberPseudo: z.string(),
        rumName: z.string(),
        text: z.string(),
    })
        .nullable(),
    perMember: z.array(z.object({
        member: memberSummarySchema,
        scores: z.array(z.object({ rumId: idSchema, overall: z.number().int() })),
        average: z.number().int().nullable(),
    })),
});
/**
 * Réordonnancement du programme.
 *
 * La liste complète des rhums de la séance, dans l'ordre voulu — et non un déplacement
 * relatif. Envoyer l'état visé plutôt qu'un mouvement rend l'appel rejouable : deux
 * requêtes identiques donnent le même programme, ce qui compte quand la connexion est
 * mauvaise en cave.
 */
export const reorderRumsSchema = z.object({
    rumIds: z.array(idSchema).min(1, 'Il faut au moins un rhum.').max(RUMS_PER_SESSION_HARD_CAP),
});
/**
 * « J'apporte une bouteille » — le geste du bouilleur.
 *
 * Ni `orderIndex` ni le droit de démasquer : la position est calculée par l'API, et
 * `isBlind` n'est qu'une demande. Basculer l'interrupteur reste une décision de régie,
 * sans quoi n'importe qui démasquerait le rhum d'un autre.
 */
export const contributeRumSchema = z.object({
    rumId: idSchema,
    isBlind: z.boolean().default(false),
});
//# sourceMappingURL=session.js.map